/**
 *
 * ## Goals
 *
 * To validate a form based on the HTML5 attributes it has, or data attribute list
 *
 * Should accept either:
 *
 * 1. A form DOM Element
 * 2. A string relating to the name of the form
 *
 * Asides – can label an form field with data-novalidate to remove it from those being validated
 *
 * @module Validate
 */

import testDefinitions, { getValidationTests } from './validate-tests';

/**
 */

const FIELDS = 'input, select, textarea',
    FIELD_GROUP_CLASS = '.form-controlGroup',
    VALIDATION_TESTS = getValidationTests(),
    MSG_ELEMENT_CLASS = 'form-message';

export default function Validate (nameOrNode, options = {}) {

    /**
     * Define options – can be passed in or set as defaults
     *
     * @namespace
     */
    this.options = {
        /** @type {boolean} */
        adjacentErrors: true,

        /** @type {boolean} */
        applySuccessClass: false,

        /** @type {string} */
        errorClass: 'has-error',

        /** @type {string} */
        successClass: 'has-success',

        /** @type {boolean} */
        focus: false,

        /** @type {boolean} */
        liveValidation: false,

        /** @type {boolean} */
        submitOnValid: true, // allows you to specify a callback and supress the form submission

        /** @type {function} */
        onSuccess: function () {},

        /** @type {function} */
        onFail: function () {}
    };


    this.form = formByNameOrNode(nameOrNode) || null;

    if (this.form === null) {
        return false;
    }

    this.options = { ...this.options, ...options || {} };
    this.fields = this.form.querySelectorAll(FIELDS);
    // this.fieldInfo = {};  would be good to add this later where this tracks state of fields (might make some of the logic simpler)
    this.errorMsgs = []; // populated but not used atm – is here with the view of being able to show all error messages in one place on a page
    this.customHandlers = {}; // custom validation functions
    this._addEvents();
}

/*
 * @private
 * Validates all the fields of the form this is called in relation to
 */
Validate.prototype._addEvents = function () {
    /*
     * Attach an event callback for the form submission
     */
    this.form.addEventListener('submit', this.validateAll.bind(this), false);
};


Validate.prototype.applyStateClass = function (element, isValid) {
    if (element) {
        element.classList.remove(this.options.errorClass);
        element.classList.remove(this.options.successClass);

        // if valid, and we apply success state to our fields
        if (isValid && this.options.applySuccessClass) {
            element.classList.add(this.options.successClass);
        } else if (!isValid) {
            element.classList.add(this.options.errorClass);
        }
    }
};

Validate.prototype.applyErrorMessage = function (field, msg) {
    // check if an error field already exists
    const errorField = findErrorField(field);

    if (errorField) {
        // matched an error msg field, so just add our error to this element
        errorField.textContent = msg;
        errorField.classList.remove('is-hidden');
    } else {
        // no match so create an error message element
        const errorEl = document.createElement('div');
        errorEl.classList.add(MSG_ELEMENT_CLASS, this.errorClass);
        errorEl.textContent = msg;
        field.after(errorEl);
    }
};

function findErrorField (field) {
    const nextSibling = field.nextElementSibling,
        controlGroup = field.closest(FIELD_GROUP_CLASS);

    // returns if finds an adjacent error field
    if (nextSibling && nextSibling.classList.contains(MSG_ELEMENT_CLASS)) {
        return nextSibling;
    }
    // returns if finds an error field in our controlGroup
    if (controlGroup) {
        const controlGroupErrorEl = controlGroup.querySelector(`.${MSG_ELEMENT_CLASS}`);

        if (controlGroupErrorEl) {
            return controlGroupErrorEl;
        }
    }
    return false; // return false if can’t find an error field
}

// remove the error message from a field (if there is one)
// Will look for an element immediately adjacent to the field first
// and fall back to looking in the controlGroup for a msg field
Validate.prototype.removeErrorMessage = function (field) {
    // is there an error message field adjacent to our field
    const nextSibling = field.nextElementSibling,
        controlGroup = field.closest(FIELD_GROUP_CLASS);

    if (nextSibling && nextSibling.matches(`.${MSG_ELEMENT_CLASS}`)) {
        nextSibling.classList.add('is-hidden');
        nextSibling.textContent = '';
    } else if (controlGroup) {
        const msgEl = controlGroup.querySelector(`.${MSG_ELEMENT_CLASS}`);

        if (msgEl) {
            msgEl.classList.add('is-hidden');
            msgEl.textContent = '';
        }
    }
};


/*
 * @public
 * Validates all the fields of the form this is called in relation to
 */
Validate.prototype.validateInput = function (field) {
    const controlGroup = field.closest(FIELD_GROUP_CLASS),
        errorElement = controlGroup || field; // if we have a controlGroup, our error element should be that, else make it the field

    let isFieldValid = true,
        errorMessage,
        customTestName;

    // don’t validate if the field is either marked as data-novalidate or is disabled
    if (!field.hasAttribute('data-novalidate') && !field.hasAttribute('disabled')) {
        // Assign custom validation function -> if custom validation attribute is defined and custom handler registered via addCustomValidationFunction
        customTestName = field.getAttribute('data-val-custom');
        if (customTestName && typeof this.customHandlers[customTestName] === 'function') {
            testDefinitions.custom.test = this.customHandlers[customTestName];
        }

        VALIDATION_TESTS.forEach(testName => {
            const testResult = testField(testName, field); // will return true if either doesn’t match criteria for a check, or if valid.  False if invalid

            if (!testResult) {
                // get error message
                errorMessage = errorMessage || getErrorMessage(testName, field);
                this.errorMsgs.push(errorMessage); // builds up an array of our errors, in case we want to display them grouped
                isFieldValid = false;
            }
        });

        this.applyStateClass(errorElement, isFieldValid); // apply a state class to our errorElement

        // Error Messaging
        if (this.options.adjacentErrors) {
            if (!isFieldValid) {
                this.applyErrorMessage(field, errorMessage);
            } else {
                this.removeErrorMessage(field);
            }
        }
    }
    return isFieldValid;
};


/*
 * @public
 * Validates all the fields of the form this is called in relation to
 */
Validate.prototype.validateAll = function (e) {

    let isFormValid = true,
        isFocused = false;

    this.errorMsgs = [];

    // cast our Nodelist to an array and loop over
    Array.from(this.fields).forEach(field => {
        if (!this.validateInput(field)) {
            isFormValid = false;
            if (this.options.focus && !isFocused) {
                field.focus();
                isFocused = true;
            }

            if (typeof this.options.onFail === 'function') {
                this.options.onFail();
            }
        }
    });

    // if we have a valid form, but we are supressing form submits
    if (e) {
        // supress form submitting if either the form is invalid or we are permanently supressing submission even when valid
        if (!isFormValid || !this.options.submitOnValid) {
            e.preventDefault();
        }
        // if the form is valid, but we are not submitting when the form is valid, call our callback function instead
        if (isFormValid
            && !this.options.submitOnValid
            && typeof this.options.onSuccess === 'function') {
            this.options.onSuccess();
        }
    }
    return isFormValid;
};

/*
 * @public
 * Registers custom validation function
 */
Validate.prototype.addCustomValidationFunction = function (name, handler) {
    var scope = this;
    if (!scope.customHandlers) {
        console.error('Custom handlers were not initialized in Validate ctor. Please check if form exists on page.');
        return;
    }

    if (name && typeof name === 'string' && handler && typeof handler === 'function') {
        scope.customHandlers[name] = handler;
    }
};

/*
 * @private
 * Determines if a form dom node was passed in or just a string representing the form name
 */
function formByNameOrNode (descriptor) {
    return (typeof descriptor === 'object') ? descriptor : document.forms[descriptor];
}


function testField (testName, field) {
    const validationTest = testDefinitions[testName];
    // check it meets the conditions for a field being tested, in case it is called independently
    if (validationTest.condition(field)) {
        const testResult = validationTest.test(field);
        if (!testResult) {
            return false;
        }
    }
    return true; // validates
}

// gets error message based on the testname, and overridden by the fields own value if that is present
function getErrorMessage (testName, field) {
    const attributeName = `data-${testDefinitions[testName].errorAttr}`;
    let customFieldMsg;

    if (field) {
        customFieldMsg = (field.hasAttribute(attributeName) && field.getAttribute(attributeName).length > 0 ? field.getAttribute(attributeName) : null);
    }

    // either return the cutomField error message (if found) or the default error based on the testName
    return (customFieldMsg !== null ? customFieldMsg : testDefinitions[testName].errorMessage);
}


window.Validate = Validate; // this can die when the old JS has been moved over to modules – only there to expose the functions to non-modules
